// Indonesian number formatting + helpers
export function formatIDR(amount) {
  const n = Math.round(Number(amount) || 0);
  const neg = n < 0;
  const s = Math.abs(n).toLocaleString("id-ID");
  return (neg ? "-Rp" : "Rp") + s;
}

export function formatAUD(amount) {
  const n = Number(amount) || 0;
  return "AUD " + n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatNumber(n) {
  return (Number(n) || 0).toLocaleString("id-ID");
}

export function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

export function formatDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

// Compress an image File to base64 (jpeg), max dimension ~1200px
const HEIC_RE = /\.(heic|heif)$/i;

async function toDecodableBlob(file, onProgress) {
  const isHeic = file.type === "image/heic" || file.type === "image/heif" || HEIC_RE.test(file.name || "");
  if (!isHeic) return file;
  onProgress && onProgress(15);
  // Convert HEIC/HEIF -> JPEG (works cross-browser via wasm)
  const heic2any = (await import("heic2any")).default;
  const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
  return Array.isArray(converted) ? converted[0] : converted;
}

function scaleDims(sw, sh, maxDim) {
  let width = sw, height = sh;
  if (width > height && width > maxDim) { height = (height * maxDim) / width; width = maxDim; }
  else if (height > maxDim) { width = (width * maxDim) / height; height = maxDim; }
  return [Math.round(width), Math.round(height)];
}

function drawToJpeg(source, w, h, quality) {
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(source, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Compress + normalize an image to a browser-friendly JPEG data URL.
 * - Converts HEIC/HEIF (iPhone) to JPEG when needed.
 * - Applies correct EXIF orientation (no upside-down photos).
 * - Accepts either a number (maxDim) or an options object for backward compat.
 */
export async function compressImage(file, opts = {}) {
  const { maxDim = 1400, quality = 0.8, onProgress } =
    typeof opts === "number" ? { maxDim: opts } : opts;
  onProgress && onProgress(5);
  const blob = await toDecodableBlob(file, onProgress);
  onProgress && onProgress(45);

  // Preferred: createImageBitmap honours EXIF orientation via imageOrientation.
  let bitmap = null;
  try {
    if (typeof createImageBitmap === "function") {
      bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
    }
  } catch (_) { bitmap = null; }

  onProgress && onProgress(70);
  if (bitmap) {
    const [w, h] = scaleDims(bitmap.width, bitmap.height, maxDim);
    const out = drawToJpeg(bitmap, w, h, quality);
    if (bitmap.close) bitmap.close();
    onProgress && onProgress(100);
    return out;
  }

  // Fallback: <img> decode (modern browsers apply EXIF orientation by default).
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const [w, h] = scaleDims(img.naturalWidth || img.width, img.naturalHeight || img.height, maxDim);
      const out = drawToJpeg(img, w, h, quality);
      URL.revokeObjectURL(url);
      onProgress && onProgress(100);
      resolve(out);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Gagal memproses gambar")); };
    img.src = url;
  });
}
